import { useEffect, useRef, useCallback, useState } from 'react';
import { TravparkLogo } from './TravparkLogo';
import './VignettOverlay.css';

/**
 * VignettOverlay - Klassisk skandinavisk broadcast-vignett for "TRAVTIPS".
 *
 * TVÅ VARIANTER:
 *  - variant="in"  (default): Full inngangs-vignett med iris-wipe til grønn
 *    chromakey på slutten. State-eier: show_vignett.
 *    0.0-8.4s : bygg opp
 *    8.4-9.7s : iris-wipe -> grønn
 *    9.7-12.5s: musikk fader ut over grønn skjerm
 *    12.5s    : POSTer show_vignett=false og forsvinner
 *
 *  - variant="out": Utro-vignett — samme animerte bygg-opp, men INGEN
 *    iris-wipe. Sluttbildet blir stående til operatøren klikker knappen
 *    igjen. State-eier: show_vignett_out.
 *    0.0-8.4s : bygg opp
 *    8.4s+    : hold ferdig scene (evig, til showVignett=false)
 *    ~2s inn : musikk starter fade over 2.2s slik at når scenen står
 *              stille er det stille (samme total musikk-lengde som "in").
 *    Merk: TRAVTIPS-tekst m/underline er ferdig-animert innen 3.4s,
 *    gullring roterer sakte kontinuerlig, videoer looper — så scenen er
 *    "levende" selv om den holder på plass.
 *
 * KRITISK: <audio> ligger på ETT konsistent sted i DOM-en (utenfor conditional
 * branches) så React ikke unmounter/remounter det mellom render-passeringer.
 */
export function VignettOverlay({ showVignett, variant = 'in' }) {
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const timersRef = useRef([]);
  const cornerVideosRef = useRef([]);
  const [visualsVisible, setVisualsVisible] = useState(false);
  const [wipeActive, setWipeActive] = useState(false);
  const [flashActive, setFlashActive] = useState(false);
  const [greenHold, setGreenHold] = useState(false);

  const attachCornerVideo = (el, idx) => {
    cornerVideosRef.current[idx] = el;
    if (!el) return;
    try {
      el.muted = true;
      el.loop = true;
      el.playsInline = true;
      const p = el.play();
      if (p && p.catch) p.catch(() => { /* browser blocked */ });
    } catch (_) { /* noop */ }
  };

  // "in" timeline
  const FLASH_AT   = 9200;
  const WIPE_AT    = 9400;
  const HIDE_AT    = 10800;
  const FADE_START = 11000;
  const FADE_DUR   = 2200;
  const FINISH_AT  = 13500;
  const START_VOL  = 0.85;

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  }, []);

  const stopAndResetAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = START_VOL;
      audio.muted = false;
    } catch (_) { /* noop */ }
  }, []);

  const stateKey = variant === 'out' ? 'show_vignett_out' : 'show_vignett';
  const finish = useCallback(() => {
    fetch('/api/overlay-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [stateKey]: false }),
    }).catch(() => { /* noop */ });
  }, [stateKey]);

  useEffect(() => {
    if (!showVignett) {
      clearAllTimers();
      setVisualsVisible(false);
      setWipeActive(false);
      setFlashActive(false);
      setGreenHold(false);
      stopAndResetAudio();
      return;
    }

    // Full reset before every fresh run
    clearAllTimers();
    stopAndResetAudio();

    setVisualsVisible(true);
    setWipeActive(false);
    setFlashActive(false);
    setGreenHold(false);

    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio.volume = START_VOL;
      audio.muted = false;
      const p = audio.play();
      if (p && p.catch) {
        p.catch(() => {
          audio.muted = true;
          audio.play().then(() => {
            setTimeout(() => { if (audioRef.current) audioRef.current.muted = false; }, 120);
          }).catch(() => { /* noop */ });
        });
      }
    }

    const add = (fn, ms) => { timersRef.current.push(setTimeout(fn, ms)); };

    // Audio fade — same timing regardless of variant. Uses audioRef.current
    // live per tick so element swap is tolerated.
    const startFadeAt = (fadeStartMs) => {
      add(() => {
        const a = audioRef.current;
        if (!a) return;
        const startVol = a.volume || START_VOL;
        const steps = 40;
        const stepMs = FADE_DUR / steps;
        let i = 0;
        fadeIntervalRef.current = setInterval(() => {
          i += 1;
          const live = audioRef.current;
          if (!live) {
            if (fadeIntervalRef.current) { clearInterval(fadeIntervalRef.current); fadeIntervalRef.current = null; }
            return;
          }
          try { live.volume = Math.max(0, startVol * (1 - i / steps)); } catch (_) { /* noop */ }
          if (i >= steps) {
            if (fadeIntervalRef.current) { clearInterval(fadeIntervalRef.current); fadeIntervalRef.current = null; }
            try { live.pause(); live.currentTime = 0; live.volume = START_VOL; } catch (_) { /* noop */ }
          }
        }, stepMs);
      }, fadeStartMs);
    };

    if (variant === 'in') {
      add(() => setFlashActive(true), FLASH_AT);
      add(() => setWipeActive(true), WIPE_AT);
      add(() => {
        setVisualsVisible(false);
        setGreenHold(true);
      }, HIDE_AT);
      startFadeAt(FADE_START);
      add(() => {
        setGreenHold(false);
        finish();
      }, FINISH_AT);
    } else {
      // 'out': ingen iris-wipe, ingen auto-finish. Bare fade musikken.
      startFadeAt(FADE_START);
      // Scene blir stående til user setter showVignett=false.
    }

    return () => { clearAllTimers(); };
  }, [showVignett, variant, finish, clearAllTimers, stopAndResetAudio]);

  return (
    <>
      {/* Audio - ALWAYS mounted at same DOM location */}
      <audio
        ref={audioRef}
        src="/assets/vignett-music.mp3"
        preload="auto"
        style={{ display: 'none' }}
        data-testid={variant === 'out' ? 'vignett-out-audio' : 'vignett-audio'}
      />

      {/* Green hold layer (only used by variant="in") */}
      {greenHold && (
        <div
          data-testid="vignett-green-hold"
          style={{
            position: 'absolute', inset: 0, backgroundColor: '#00FF00', zIndex: 300,
          }}
        />
      )}

      {visualsVisible && (
        <div data-testid={variant === 'out' ? 'vignett-out-overlay' : 'vignett-overlay'} className="vignett-root">
          <div className="vignett-bg" />

          <div className="vignett-corner vignett-corner-tl">
            <video className="vignett-corner-video" ref={(el) => attachCornerVideo(el, 0)} muted loop playsInline preload="auto">
              <source src="/assets/trav1.webm" type="video/webm" />
              <source src="/assets/trav1.mp4" type="video/mp4" />
            </video>
            <div className="vignett-corner-tint" />
          </div>
          <div className="vignett-corner vignett-corner-tr">
            <video className="vignett-corner-video" ref={(el) => attachCornerVideo(el, 1)} muted loop playsInline preload="auto">
              <source src="/assets/trav2.webm" type="video/webm" />
              <source src="/assets/trav2.mp4" type="video/mp4" />
            </video>
            <div className="vignett-corner-tint" />
          </div>
          <div className="vignett-corner vignett-corner-bl">
            <video className="vignett-corner-video" ref={(el) => attachCornerVideo(el, 2)} muted loop playsInline preload="auto">
              <source src="/assets/trav3.webm" type="video/webm" />
              <source src="/assets/trav3.mp4" type="video/mp4" />
            </video>
            <div className="vignett-corner-tint" />
          </div>
          <div className="vignett-corner vignett-corner-br">
            <video className="vignett-corner-video" ref={(el) => attachCornerVideo(el, 3)} muted loop playsInline preload="auto">
              <source src="/assets/trav4.webm" type="video/webm" />
              <source src="/assets/trav4.mp4" type="video/mp4" />
            </video>
            <div className="vignett-corner-tint" />
          </div>

          <div className="vignett-topline" />
          <div className="vignett-ring" />

          <div className="vignett-logo-wrap">
            <TravparkLogo height={240} />
          </div>

          <div className="vignett-text" data-testid={variant === 'out' ? 'vignett-out-text' : 'vignett-text'}>
            {'TRAVTIPS'.split('').map((c, i) => (
              <span key={i} style={{ '--letter-delay': `${1.6 + i * 0.20}s` }}>{c}</span>
            ))}
          </div>

          <div className="vignett-underline" />

          {variant === 'in' && <div className={`vignett-flash ${flashActive ? 'active' : ''}`} />}
          {variant === 'in' && <div className={`vignett-iris ${wipeActive ? 'active' : ''}`} data-testid="vignett-iris" />}
        </div>
      )}
    </>
  );
}
